import { Query, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  PaginatedThreediModelTaskList,
  ThreediModel,
  ThreediModelTask,
  ThreedimodelsListRequest,
} from "@nens/threedi/openapi-client";

import { getSchematisationsApi, getThreediModelsApi } from "../apiConfiguration";

import { ID } from "./queryClient";
import { DeleteSchematisationRevisionItem } from "./schematisations";

const threediModelsApi = getThreediModelsApi();
const schematisationsApi = getSchematisationsApi();

// Hook to fetch a single threedimodel
export function useThreediModel(threediModelId: ID) {
  return useQuery({
    queryKey: ["threedimodel", threediModelId],
    queryFn: () => {
      if (!threediModelId) throw new Error("threediModelId is required");
      return threediModelsApi.threedimodelsRead({ id: threediModelId });
    },
    enabled: !!threediModelId,
  });
}

export function useThreediModelsByOwner(uniqueId: string, request: ThreedimodelsListRequest = {}) {
  return useQuery({
    queryKey: ["threedimodels", uniqueId, request],
    queryFn: async () => {
      return await threediModelsApi.threedimodelsList({
        revisionSchematisationOwnerUniqueId: uniqueId,
        ...request,
      });
    },
  });
}

const MAX_FETCH = 10000;

export function useAllThreediModelsByOwner(
  uniqueId: string,
  requestBoth: boolean,
  request: ThreedimodelsListRequest = {}
) {
  return useQuery({
    queryKey: ["allThreedimodels", uniqueId, requestBoth, request],
    queryFn: async () => {
      let threediModels: ThreediModel[] = [];
      let offset = 0;

      // Models have a foreign key to Organisation ("owner") either through revisionSchematisation
      // (new situation), or by revisionRepository (old situation).
      // If requestBoth is true we get both, otherwise only the new.

      const getAllThreediModelsByOwner = async (
        resultIdKey:
          | "revisionSchematisationOwnerUniqueId"
          | "revisionRepositoryOrganisationUniqueId"
      ) => {
        while (true) {
          const results = await threediModelsApi.threedimodelsList({
            ...request,
            offset,
            [resultIdKey]: uniqueId,
            limit: MAX_FETCH,
          });

          threediModels = threediModels.concat(results.results);

          if (results.next && offset <= MAX_FETCH - offset) {
            offset += results.results.length;
          } else {
            break;
          }
        }
      };

      await Promise.all([
        getAllThreediModelsByOwner("revisionSchematisationOwnerUniqueId"),
        requestBoth
          ? getAllThreediModelsByOwner("revisionRepositoryOrganisationUniqueId")
          : undefined,
      ]);
      return threediModels;
    },
  });
}

const deleteMultipleModels = async (modelsIds: ID[]) =>
  await Promise.all(
    modelsIds.map(async (modelId) => {
      if (modelId)
        return threediModelsApi.threedimodelsDelete({
          id: modelId,
        });
    })
  );

export function useDeleteMultipleModelsBySchematisationRevisions(
  schemas: DeleteSchematisationRevisionItem[]
) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    const modelsResponse = await Promise.all(
      schemas.map(async (schema) => {
        if (schema.schematisationId && schema.revisionId)
          return schematisationsApi.schematisationsRevisionsThreedimodels({
            id: schema.revisionId,
            schematisationPk: schema.schematisationId,
          });

        return;
      })
    );

    const modelsIds = modelsResponse.flat().flatMap((model) => (model ? model.id : undefined));
    await deleteMultipleModels(modelsIds);

    queryClient.invalidateQueries({ queryKey: ["threedimodels"] });
    queryClient.invalidateQueries({ queryKey: ["schematisation-revisions"] });
    queryClient.invalidateQueries({ queryKey: ["revisions"] });
    queryClient.invalidateQueries({ queryKey: ["allThreedimodels"] });
  }, [queryClient, schemas]);
}

export function useDeleteMultipleModels(modelsIds: ID[]) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await deleteMultipleModels(modelsIds);

    queryClient.invalidateQueries({ queryKey: ["threedimodels"] });
    queryClient.invalidateQueries({ queryKey: ["allThreedimodels"] });
  }, [queryClient, modelsIds]);
}

export function taskIsFinished(task: ThreediModelTask): boolean {
  return task.status === "success" || task.status === "failure" || task.status === "revoked";
}

export function useModelTasks(threediModelId: number | undefined) {
  return useQuery({
    queryKey: ["modeltasks", threediModelId],
    queryFn: () => {
      if (!threediModelId) throw new Error("threediModelId is required");
      return threediModelsApi.threedimodelsTasksList({
        threedimodelPk: threediModelId,
      });
    },
    refetchInterval: (query: Query<PaginatedThreediModelTaskList>) => {
      // If all tasks are finished, we refresh slowly, using the default (undefined).
      // Otherwise, quickly -- every second.
      const QUICK_REFETCH = 1000; // ms
      const currentData = query.state.data;

      if (currentData === undefined) {
        return QUICK_REFETCH;
      }

      const allFinished = currentData.results.every(taskIsFinished);

      return allFinished ? false : QUICK_REFETCH;
    },
    enabled: !!threediModelId,
  });
}

export async function recreateThreediModel(
  threediModel: ThreediModel,
  callback: (message: string[]) => void
) {
  // Delete threedi model, then create a new one from the same schematisation and revision,
  // then call the callback.
  try {
    if (!threediModel.schematisationId) {
      callback(["Missing schematisationId on threediModel"]);
      return;
    }
    if (!threediModel.id || !threediModel.revisionId) {
      callback(["Missing id or revisionId on threediModel"]);
      return;
    }
    await threediModelsApi.threedimodelsDelete({ id: threediModel.id });
    await schematisationsApi.schematisationsRevisionsCreateThreedimodel({
      id: threediModel.revisionId,
      schematisationPk: threediModel.schematisationId,
      data: { inheritFromPreviousRevision: true },
    });
    callback([]);
  } catch (error: any) {
    if (error && error.response) {
      const body = await error.response.json();
      callback(body);
    } else {
      callback([]);
    }
  }
}

export async function deleteThreediModel(
  threediModel: ThreediModel,
  callback: (message: string[]) => void
) {
  // Delete threedi model
  // then call the callback.
  try {
    if (!threediModel.id) {
      callback(["Missing id on threediModel"]);
      return;
    }
    await threediModelsApi.threedimodelsDelete({ id: threediModel.id });
    callback([]);
  } catch (error: any) {
    if (error && error.response) {
      const body = await error.response.json();
      callback(body);
    } else {
      callback([]);
    }
  }
}

export function useGridadmin(threediModelId: number | undefined) {
  return useQuery({
    queryKey: ["gridadmin", threediModelId],
    queryFn: () => {
      if (!threediModelId) throw new Error("threediModelId is required");
      return threediModelsApi.threedimodelsGridadminDownload({ id: threediModelId });
    },
    enabled: threediModelId !== undefined,
  });
}

export function useModelsWithIds(ids: (number | undefined)[]) {
  const threediModelsApi = getThreediModelsApi();
  const results = useQueries({
    queries: ids.map((id) => {
      return {
        queryKey: ["getModelById", id],
        queryFn: () =>
          id ? threediModelsApi.threedimodelsRead({ id: id }) : Promise.resolve(null),
      };
    }),
  });

  return results;
}
